/**
 * Graph Helpers - Conversão de dados API → Cytoscape.js
 */

import type { Investigation, No, Aresta, TipoNo } from '../types/investigation';
import type { CytoscapeElement, CytoscapeNodeData, CytoscapeEdgeData } from '../types/graph';
import { truncate } from './formatters';

/**
 * Converte investigação da API para elementos Cytoscape
 */
export function apiToCytoscape(investigation: Investigation): CytoscapeElement[] {
  const elements: CytoscapeElement[] = [];

  // Converter nós
  investigation.grafo.nos.forEach((no) => {
    elements.push({
      group: 'nodes' as const,
      data: {
        id: no.id,
        label: formatNodeLabel(no),
        type: no.tipo,
        expandido: no.expandido,
        nivel: no.nivel,
        rawData: no.dados,
      } as CytoscapeNodeData,
      classes: no.expandido ? 'expandido' : 'nao-expandido',
    });
  });

  // Converter arestas
  investigation.grafo.arestas.forEach((aresta) => {
    elements.push({
      group: 'edges' as const,
      data: {
        id: aresta.id,
        source: aresta.origem,
        target: aresta.destino,
        label: truncate(aresta.label, 30), // Limitar tamanho do label
        type: aresta.tipo,
      } as CytoscapeEdgeData,
    });
  });

  return elements;
}

/**
 * Formata label do nó baseado no tipo
 */
function formatNodeLabel(no: No): string {
  switch (no.tipo) {
    case 'cnpj':
      // Usa razão social se disponível, senão CNPJ
      if (no.dados.razao_social) {
        return truncate(no.dados.razao_social, 25);
      }
      // Formata CNPJ: 12345678 → 12.345.678
      const cnpj = no.valor.substring(0, 8);
      return cnpj.replace(/^(\d{2})(\d{3})(\d{3})/, '$1.$2.$3');

    case 'pessoa':
      // Primeiros 2 nomes
      const nomes = no.valor.split(' ');
      const primeirosDois = nomes.slice(0, 2).join(' ');
      return truncate(primeirosDois, 20);

    case 'email':
      return truncate(no.valor, 20);

    case 'telefone':
      return no.valor;

    case 'cpf-nome':
      return truncate(no.valor, 20);

    default:
      return truncate(no.valor, 20);
  }
}

/**
 * Estilos Cytoscape.js
 */
export const cytoscapeStylesheet = [
  // Nós - CNPJ
  {
    selector: 'node[type="cnpj"]',
    style: {
      'background-color': '#3B82F6', // blue-500
      'label': 'data(label)',
      'width': 60,
      'height': 60,
      'font-size': 12,
      'text-valign': 'bottom',
      'text-margin-y': 5,
      'text-wrap': 'wrap',
      'text-max-width': 100,
      'color': '#1F2937', // gray-800
      'text-background-opacity': 0.8,
      'text-background-color': '#FFFFFF',
      'text-background-padding': 3,
    },
  },
  // Nós - Pessoa
  {
    selector: 'node[type="pessoa"]',
    style: {
      'background-color': '#10B981', // green-500
      'shape': 'ellipse',
      'width': 50,
      'height': 50,
      'label': 'data(label)',
      'font-size': 11,
      'text-valign': 'bottom',
      'text-margin-y': 5,
      'color': '#1F2937',
      'text-background-opacity': 0.8,
      'text-background-color': '#FFFFFF',
      'text-background-padding': 3,
    },
  },
  // Nós - Email
  {
    selector: 'node[type="email"]',
    style: {
      'background-color': '#F59E0B', // amber-500
      'shape': 'triangle',
      'width': 40,
      'height': 40,
      'label': 'data(label)',
      'font-size': 10,
      'text-valign': 'bottom',
      'text-margin-y': 5,
      'color': '#1F2937',
      'text-background-opacity': 0.8,
      'text-background-color': '#FFFFFF',
      'text-background-padding': 3,
    },
  },
  // Nós - Telefone
  {
    selector: 'node[type="telefone"]',
    style: {
      'background-color': '#8B5CF6', // violet-500
      'shape': 'triangle',
      'width': 40,
      'height': 40,
      'label': 'data(label)',
      'font-size': 10,
      'text-valign': 'bottom',
      'text-margin-y': 5,
      'color': '#1F2937',
      'text-background-opacity': 0.8,
      'text-background-color': '#FFFFFF',
      'text-background-padding': 3,
    },
  },
  // Nós - CPF-Nome
  {
    selector: 'node[type="cpf-nome"]',
    style: {
      'background-color': '#EC4899', // pink-500
      'shape': 'diamond',
      'width': 45,
      'height': 45,
      'label': 'data(label)',
      'font-size': 10,
      'text-valign': 'bottom',
      'text-margin-y': 5,
      'color': '#1F2937',
      'text-background-opacity': 0.8,
      'text-background-color': '#FFFFFF',
      'text-background-padding': 3,
    },
  },
  // Nós - Selecionado
  {
    selector: 'node:selected',
    style: {
      'border-width': 4,
      'border-color': '#EF4444', // red-500
      'overlay-color': '#EF4444',
      'overlay-padding': 10,
      'overlay-opacity': 0.2,
    },
  },
  // Nós - Não expandido (borda tracejada)
  {
    selector: 'node.nao-expandido',
    style: {
      'border-width': 3,
      'border-style': 'dashed',
      'border-color': '#6B7280', // gray-500
    },
  },
  // Nós - Hover (desktop)
  {
    selector: 'node:active',
    style: {
      'overlay-color': '#3B82F6',
      'overlay-padding': 8,
      'overlay-opacity': 0.15,
    },
  },
  // Arestas - Estilo padrão
  {
    selector: 'edge',
    style: {
      'width': 2,
      'line-color': '#9CA3AF', // gray-400
      'target-arrow-color': '#9CA3AF',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'label': 'data(label)',
      'font-size': 9,
      'text-rotation': 'autorotate',
      'text-margin-y': -10,
      'color': '#4B5563', // gray-600
      'text-background-opacity': 0.7,
      'text-background-color': '#FFFFFF',
      'text-background-padding': 2,
    },
  },
  // Arestas - Selecionada
  {
    selector: 'edge:selected',
    style: {
      'width': 3,
      'line-color': '#3B82F6',
      'target-arrow-color': '#3B82F6',
    },
  },
  // Nós e arestas - Faded (usado no highlight de vizinhos)
  {
    selector: '.faded',
    style: {
      'opacity': 0.2,
    },
  },
  // Nós e arestas - Highlighted (usado no highlight de vizinhos)
  {
    selector: '.highlighted',
    style: {
      'opacity': 1,
      'z-index': 999,
    },
  },
];

/**
 * Configuração de layout padrão (fcose - force-directed)
 */
export const defaultLayoutConfig = {
  name: 'fcose',
  quality: 'default',
  animate: true,
  animationDuration: 1000,
  animationEasing: 'ease-out',
  fit: true,
  padding: 50,
  nodeDimensionsIncludeLabels: true,
  randomize: false,
  // Parâmetros físicos
  nodeRepulsion: 4500,
  idealEdgeLength: 100,
  edgeElasticity: 0.45,
  nestingFactor: 0.1,
  gravity: 0.25,
  numIter: 2500,
  // Incremental layout
  tile: true,
  tilingPaddingVertical: 10,
  tilingPaddingHorizontal: 10,
  gravityRangeCompound: 1.5,
  gravityCompound: 1.0,
  gravityRange: 3.8,
};

/**
 * Filtra elementos do grafo baseado em critérios
 */
export function filterGraphElements(
  elements: CytoscapeElement[],
  filter: {
    tipos?: TipoNo[];
    busca?: string;
    mostrarExpandidos?: boolean;
    mostrarNaoExpandidos?: boolean;
    nivelMinimo?: number;
    nivelMaximo?: number;
  }
): CytoscapeElement[] {
  return elements.filter((element) => {
    // Arestas sempre passam (filtro é apenas para nós)
    if (element.group === 'edges') return true;

    const nodeData = element.data as CytoscapeNodeData;

    // Filtro por tipo
    if (filter.tipos && filter.tipos.length > 0) {
      if (!filter.tipos.includes(nodeData.type)) return false;
    }

    // Filtro por busca textual
    if (filter.busca) {
      const searchLower = filter.busca.toLowerCase();
      const labelMatch = nodeData.label.toLowerCase().includes(searchLower);
      const valueMatch = JSON.stringify(nodeData.rawData)
        .toLowerCase()
        .includes(searchLower);
      if (!labelMatch && !valueMatch) return false;
    }

    // Filtro por status de expansão
    if (filter.mostrarExpandidos === false && nodeData.expandido) return false;
    if (filter.mostrarNaoExpandidos === false && !nodeData.expandido) return false;

    // Filtro por nível
    if (filter.nivelMinimo !== undefined && nodeData.nivel < filter.nivelMinimo) {
      return false;
    }
    if (filter.nivelMaximo !== undefined && nodeData.nivel > filter.nivelMaximo) {
      return false;
    }

    return true;
  });
}

/**
 * Calcula estatísticas do grafo (client-side)
 */
export function calculateGraphStats(elements: CytoscapeElement[]) {
  const nodes = elements.filter((el) => el.group === 'nodes');
  const edges = elements.filter((el) => el.group === 'edges');

  const nodesByType: Record<string, number> = {};
  nodes.forEach((node) => {
    const type = (node.data as CytoscapeNodeData).type;
    nodesByType[type] = (nodesByType[type] || 0) + 1;
  });

  const totalDegree = nodes.length * 2; // Aproximação simples
  const averageDegree = nodes.length > 0 ? edges.length / nodes.length : 0;

  return {
    totalNodes: nodes.length,
    totalEdges: edges.length,
    nodesByType,
    averageDegree: Math.round(averageDegree * 10) / 10,
  };
}
